import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { LuEye } from 'react-icons/lu';
import { adminAPI } from '../../../services/api';
import { formatDate } from '../../../utils/formatters';
import LoadingSpinner from '../../../components/LoadingSpinner';
import ErrorAlert from '../../../components/ErrorAlert';
import InventoryStylePagination from '../../../components/InventoryStylePagination';
import { useAuth } from '../../../contexts/AuthContext';
import { hasAdminManagerRole } from '../../../utils/roles';

// Admin ledger of inventory corrections (receipt voids now; adjustments and
// transfers later). Documents are immutable -- this is a read-only history
// with a billing-review filter for ops follow-up.
const TYPE_LABELS = {
  receipt_void: 'Receipt Void',
  quantity_adjustment: 'Adjustment',
  warehouse_transfer: 'Transfer'
};

const CorrectionsList = () => {
  const { user } = useAuth();
  const canCorrect = hasAdminManagerRole(user);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState({
    correctionType: '',
    requiresBillingReview: false,
    companyId: ''
  });

  const { data, isLoading, isError, error } = useQuery(
    ['admin-corrections', page, limit, filters],
    () => adminAPI.getCorrections({
      page,
      limit,
      correctionType: filters.correctionType || undefined,
      requiresBillingReview: filters.requiresBillingReview ? 'true' : undefined,
      companyId: filters.companyId || undefined
    }),
    { keepPreviousData: true }
  );

  // The customers endpoint returns { customers, pagination } and paginates
  // to 10 by default -- request enough rows for a filter dropdown.
  const { data: customersData } = useQuery(
    'customers-filter-options',
    () => adminAPI.getCustomers({ limit: 500 }),
    { staleTime: 5 * 60 * 1000 }
  );

  const customers = customersData?.customers || [];
  const corrections = data?.data || [];

  if (isError) {
    return <ErrorAlert message={error?.message || 'Failed to load corrections'} />;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Inventory Corrections</h1>
          <p className="mt-1 text-sm text-gray-500">
            Receipt voids and stock corrections, with billing follow-ups flagged for review.
          </p>
        </div>
        {/* Correction mutations are role-gated on the backend (RF15). */}
        {canCorrect && (
        <div className="mt-3 sm:mt-0 flex gap-2">
          <Link
            to="/admin/corrections/transfers/new"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            New Transfer
          </Link>
          <Link
            to="/admin/corrections/adjustments/new"
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            New Adjustment
          </Link>
        </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg mb-6 p-4 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
          <select
            value={filters.correctionType}
            onChange={(e) => { setFilters({ ...filters, correctionType: e.target.value }); setPage(1); }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">All types</option>
            <option value="receipt_void">Receipt Void</option>
            <option value="quantity_adjustment">Adjustment</option>
            <option value="warehouse_transfer">Transfer</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Customer</label>
          <select
            value={filters.companyId}
            onChange={(e) => { setFilters({ ...filters, companyId: e.target.value }); setPage(1); }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="">All customers</option>
            {(Array.isArray(customers) ? customers : []).map((customer) => (
              <option key={customer.id} value={customer.id}>{customer.name}</option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
          <input
            type="checkbox"
            checked={filters.requiresBillingReview}
            onChange={(e) => { setFilters({ ...filters, requiresBillingReview: e.target.checked }); setPage(1); }}
            className="h-4 w-4 rounded border-gray-300"
          />
          Needs billing review
        </label>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-8"><LoadingSpinner /></div>
        ) : corrections.length === 0 ? (
          <p className="p-8 text-sm text-gray-500 text-center">No corrections found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Correction #</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billing</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {corrections.map((correction) => (
                  <tr key={correction.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{correction.correction_number}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{TYPE_LABELS[correction.correction_type] || correction.correction_type}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{correction.reason_code || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(correction.created_at || correction.createdAt)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{correction.CreatedBy?.name || '—'}</td>
                    <td className="px-4 py-3 text-sm">
                      {correction.requires_billing_review ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Needs review
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Handled
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/admin/corrections/${correction.id}`}
                        className="text-indigo-600 hover:text-indigo-800 inline-flex items-center"
                        title="View correction"
                      >
                        <LuEye className="h-4 w-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <InventoryStylePagination
          currentPage={page}
          totalPages={data?.totalPages || 1}
          totalItems={data?.total || 0}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={(newLimit) => { setLimit(newLimit); setPage(1); }}
        />
      </div>
    </div>
  );
};

export default CorrectionsList;
